import React from "react";
import { CalendarIcon } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MonthPicker as CalendarMonthPicker } from "@/components/ui/monthpicker";
import { ClearButton } from "@/components/ui/ClearButton";
import { cn } from "@/utils";

type CalendarProps = React.ComponentProps<typeof CalendarMonthPicker>;

const normalizeMonth = (input?: Date | null) => {
  if (!input || !isValid(input)) return undefined;
  const normalized = new Date(input);
  normalized.setDate(1);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const defaultDisplayFormatter = (month: Date) => format(month, "MMM yyyy");
const defaultValueFormatter = (month: Date) => format(month, "yyyy-MM");
const defaultValueParser = (value: string) => normalizeMonth(parseISO(value));

export type MonthPickerProps = {
  value?: Date | string | null;
  onChange?: (month?: Date) => void;
  onValueChange?: (value?: string) => void;
  placeholder?: string;
  allowClear?: boolean;
  displayFormatter?: (month: Date) => string;
  valueFormatter?: (month: Date) => string;
  valueParser?: (value: string) => Date | undefined;
  buttonClassName?: string;
  wrapperClassName?: string;
  invalid?: boolean;
  calendarClassName?: string;
  popoverContentClassName?: string;
  disabled?: boolean;
  closeOnSelect?: boolean;
  clearAriaLabel?: string;
  ariaLabel?: string;
} & Omit<CalendarProps, "selectedDate" | "onDateSelect" | "className">;

export const MonthPicker: React.FC<MonthPickerProps> = ({
  value,
  onChange,
  onValueChange,
  placeholder = "MM/YYYY",
  allowClear = true,
  displayFormatter,
  valueFormatter,
  valueParser,
  buttonClassName,
  wrapperClassName,
  invalid = false,
  calendarClassName,
  popoverContentClassName,
  disabled = false,
  closeOnSelect = true,
  clearAriaLabel = "Clear month",
  ariaLabel,
  ...calendarProps
}) => {
  const [open, setOpen] = React.useState(false);

  const parser = React.useMemo(() => valueParser ?? defaultValueParser, [valueParser]);
  const outputFormatter = React.useMemo(() => valueFormatter ?? defaultValueFormatter, [valueFormatter]);
  const labelFormatter = React.useMemo(
    () => displayFormatter ?? defaultDisplayFormatter,
    [displayFormatter]
  );

  const parsedValue = React.useMemo(() => {
    if (value === null || value === undefined) return undefined;
    if (value instanceof Date) return normalizeMonth(value);
    if (typeof value === "string") {
      const parsed = parser(value);
      return parsed ? normalizeMonth(parsed) : undefined;
    }
    return undefined;
  }, [parser, value]);

  const buttonLabel = parsedValue ? labelFormatter(parsedValue) : placeholder;
  const buttonAriaLabel =
    ariaLabel ??
    (parsedValue ?
      `Change month, current selection ${labelFormatter(parsedValue)}`
    : "Open month picker");

  const shouldShowClear = allowClear && !disabled && !!parsedValue;

  const handleClose = React.useCallback(() => setOpen(false), []);
  const emitChange = React.useCallback(
    (next?: Date) => {
      const normalized = next ? normalizeMonth(next) : undefined;
      onChange?.(normalized);
      onValueChange?.(normalized ? outputFormatter(normalized) : undefined);
    },
    [onChange, onValueChange, outputFormatter]
  );

  const handleSelect = React.useCallback(
    (next?: Date) => {
      emitChange(next);
      if (closeOnSelect && next) handleClose();
    },
    [closeOnSelect, emitChange, handleClose]
  );

  const handleClear = React.useCallback(() => {
    emitChange(undefined);
    handleClose();
  }, [emitChange, handleClose]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (disabled && nextOpen) return;
      setOpen(nextOpen);
    },
    [disabled]
  );

  return (
    <div className={cn("w-full", wrapperClassName)}>
      <div className="relative">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              className={cn(
                "relative !flex w-full items-center justify-between rounded-md bg-white pl-4 text-left text-sm font-medium shadow-none hover:bg-white focus-visible:ring-0 focus-visible:ring-offset-0",
                "min-h-[36px]",
                !parsedValue ? "text-gray-400" : "text-gray-700",
                invalid &&
                  "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/30",
                shouldShowClear ? "pr-8" : "pr-4",
                buttonClassName
              )}
              aria-invalid={invalid || undefined}
              aria-label={buttonAriaLabel}
            >
              <span className="flex-1 truncate">{buttonLabel}</span>
              <span className="flex items-center gap-2 text-[#9CA3AF] shrink-0">
                <CalendarIcon className="h-4 w-4" />
              </span>
            </Button>
          </PopoverTrigger>

          <PopoverContent className={cn("w-auto p-0", popoverContentClassName)} align="start">
            <CalendarMonthPicker
              {...calendarProps}
              className={calendarClassName}
              selectedDate={parsedValue}
              onDateSelect={handleSelect}
            />
          </PopoverContent>
        </Popover>
        {shouldShowClear && (
          <ClearButton
            onClick={handleClear}
            ariaLabel={clearAriaLabel}
            title={clearAriaLabel}
            className="absolute right-3 top-1/2 z-10 -translate-y-1/2"
          />
        )}
      </div>
    </div>
  );
};
